import { Spinner } from '@farol-ds/react'
import classNames from 'classnames'
import { forwardRef, useState } from 'react'

import { ErrorBoundary } from './components/error-boundary'
import { EntityType } from './config'
import styles from './doc-popover.module.scss'
import { PopoverHover, PopoverHoverElement } from './popover-hover'
import {
  ALL_SELECTORS,
  Entity,
  getElementsFilteredByAttributes,
  renderContentFromEntity,
  updateEntityFromElement,
} from './utils'

export type DocPopoverElement = PopoverHoverElement
export interface DocPopoverProps {
  disableTouchFeatures: boolean | undefined
  contentRef: React.RefObject<HTMLElement>
  context?: string
  hasLRSubscription?: boolean
  popoverEntityType?: EntityType
  checkPopoverAccess?: (rel: string) => Promise<boolean>
}

export const DocPopover = forwardRef<DocPopoverElement, DocPopoverProps>(function DocPopover(
  props,
  forwardedRef
) {
  const {
    disableTouchFeatures,
    contentRef,
    popoverEntityType,
    checkPopoverAccess = async () => true,
  } = props

  const [entity, setEntity] = useState<Entity | null | undefined>()
  const hasError = entity === null

  function onError(_err: Error) {
    setEntity(null)
  }

  if (disableTouchFeatures) {
    return null
  }

  return (
    <ErrorBoundary onError={onError}>
      <PopoverHover
        ref={forwardedRef}
        getElementNodes={() => {
          const selector = ALL_SELECTORS.join(', ')
          const elements = contentRef.current?.querySelectorAll<HTMLElement>(selector) ?? []
          return getElementsFilteredByAttributes({
            elements,
            attributes: ['rel', 'data-profile-tooltip'],
          })
        }}
        onOpenChange={async (open, element) => {
          const rel = element?.getAttribute('rel') || ''
          const isCustomLock = element?.getAttribute('data-meta') === 'custom'
          const canViewPopoverContent = isCustomLock ? await checkPopoverAccess?.(rel) : true
          if (canViewPopoverContent && open && element) {
            updateEntityFromElement(element, {
              setEntity,
              onError,
              popoverEntityType,
            })
          } else {
            setEntity(undefined)
          }
        }}
        open={hasError ? false : undefined}
        // TODO - move padding to a wrapper @ronilygomes
        className={classNames(styles.popover, {
          [styles.popoverDisablePadding]: entity?.type === 'PROFILE_QUICKVIEW' && entity?.loaded,
        })}
        disablePadding
      >
        {entity?.loaded ? (
          <>{renderContentFromEntity(entity, props.context, props.hasLRSubscription)}</>
        ) : (
          <Spinner data-testid="doc-popover-spinner" />
        )}
      </PopoverHover>
    </ErrorBoundary>
  )
})
